import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggingService } from '../shared/services/logging/logging.service';
import * as fromDataServices from '../data-services';
import { getDynamicColumns } from '../shared';

@Injectable({
    providedIn: 'root'
})
export class ProjectBudgetService {
    constructor(private http: HttpClient, private log: LoggingService) {}
    filterItems = ['Status', 'AutoCalculateAmount', 'Row_Version', 'updates'];

    private uri = `${environment.apiLocation}/ProjectBudget`;

    public getDisbursementAmount(value: number): number {
        return Number((Math.floor(value * 100) / 100).toFixed(2));
    }

    public applyDisbursement(
        item: fromDataServices.ProjectBudget.ProjectBudget
    ): fromDataServices.ProjectBudget.ProjectBudget {
        const cols = this.getPeriodColumns(item);
        const amounts = this.getDisbursementAmount(
            (item.DisburseAmount || 0) / (cols.length || 1)
        );
        const hours = this.getDisbursementAmount(
            (item.DisburseHours || 0) / (cols.length || 1)
        );
        cols.forEach(c => {
            item[c].Hours = hours;
            item[c].Amount = amounts;
        });
        item.DisburseAmount = this.getDisbursementAmount(amounts * cols.length);
        item.DisburseHours = this.getDisbursementAmount(hours * cols.length);
        return item;
    }

    public createAndDisburse(request: {
        item: Record<string, unknown>;
        contractListId: string;
        budgetStartDate: string | null;
        budgetEndDate: string | null;
        disburseHours: number;
        disburseAmount: number;
    }): Observable<unknown> {
        const createItem = {
            ContractListID: request.contractListId,
            TaskListID: request.item.TaskListID ?? null,
            CostType: request.item.CostType ?? null,
            SIListID: request.item.SIListID ?? null,
            PIListID: request.item.PIListID ?? null,
            CostCenterID: request.item.CostCenterID ?? null,
            PersonListID: request.item.PersonListID ?? null,
            BudgetStartDate: request.budgetStartDate,
            BudgetEndDate: request.budgetEndDate,
            JumpStart: false
        };
        this.log.info({
            type: 'ProjectBudgetService:createAndDisburse:start',
            createItem,
            disburseHours: request.disburseHours,
            disburseAmount: request.disburseAmount
        });
        return this.putBudgetList([createItem]).pipe(
            delay(400),
            switchMap(() =>
                this.getMappedBudgets(
                    request.contractListId,
                    request.budgetStartDate,
                    request.budgetEndDate
                )
            ),
            switchMap(budgets => {
                if (request.disburseHours <= 0 && request.disburseAmount <= 0) {
                    return of(budgets);
                }
                const created = this.findCreatedBudget(budgets, request.item);
                const periods = created ? this.getPeriodColumns(created) : [];
                this.log.info({
                    type: 'ProjectBudgetService:createAndDisburse:afterCreate',
                    created,
                    periodCount: periods.length,
                    budgetCount: budgets.length
                });
                if (created && periods.length) {
                    return this.putPeriodDisbursement(created, request);
                }
                return timer(1000).pipe(
                    switchMap(() =>
                        this.getMappedBudgets(
                            request.contractListId,
                            request.budgetStartDate,
                            request.budgetEndDate
                        )
                    ),
                    switchMap(retryBudgets => {
                        const retryCreated = this.findCreatedBudget(
                            retryBudgets,
                            request.item
                        );
                        const retryPeriods = retryCreated
                            ? this.getPeriodColumns(retryCreated)
                            : [];
                        this.log.info({
                            type: 'ProjectBudgetService:createAndDisburse:retry',
                            created: retryCreated,
                            periodCount: retryPeriods.length
                        });
                        if (!retryCreated || !retryPeriods.length) {
                            this.log.error({
                                type: 'ProjectBudgetService:createAndDisburse:noPeriods',
                                item: request.item,
                                retryBudgets
                            });
                            return of(retryBudgets);
                        }
                        return this.putPeriodDisbursement(
                            retryCreated,
                            request
                        );
                    })
                );
            })
        );
    }

    public delete(
        item: fromDataServices.ProjectBudget.ProjectBudget,
        BudgetStartDate: Date,
        BudgetEndDate: Date
    ): Observable<fromDataServices.ProjectBudget.ProjectBudget> {
        this.log.verbose({
            type: 'ProjectBudgetService:deleteBudget',
            uri: this.uri,
            item,
            BudgetStartDate: BudgetStartDate,
            BudgetEndDate: BudgetEndDate
        });
        const cols = getDynamicColumns(item, this.filterItems);
        const updates = [] as any[];
        cols.forEach(c => {
            const keys = Object.keys(item[c]);
            if (item[c].TxnID) {
                const updateItem = {};
                keys.forEach(k => {
                    updateItem[k.replace(' ', '')] = item[c][k];
                });
                updates.push({
                    ...updateItem
                });
            }
        });
        return this.http
            .delete<fromDataServices.ProjectBudget.ProjectBudget>(this.uri, {
                body: {
                    TxnID: updates[0].TxnID,
                    BudgetStartDate: BudgetStartDate,
                    BudgetEndDate: BudgetEndDate
                }
            })
            .pipe(
                tap(res =>
                    this.log.verbose({
                        type: 'ProjectBudgetService:response',
                        res
                    })
                )
            );
    }

    private putPeriodDisbursement(
        created: any,
        request: {
            contractListId: string;
            budgetStartDate: string | null;
            budgetEndDate: string | null;
            disburseHours: number;
            disburseAmount: number;
        }
    ): Observable<unknown> {
        const periods = this.getPeriodColumns(created);
        const hours = this.getDisbursementAmount(
            request.disburseHours / (periods.length || 1)
        );
        const amount = this.getDisbursementAmount(
            request.disburseAmount / (periods.length || 1)
        );
        const updates = periods.map(col => ({
            TxnID: created[col].TxnID,
            Hours: String(hours),
            Amount: String(amount),
            CostType: created.CostType,
            ContractListID: request.contractListId,
            TaskListID: created.TaskListID,
            SIListID: created.SIListID ?? null,
            PersonListID: created.PersonListID ?? null,
            PIListID: created.PIListID ?? null,
            CostCenterID: created.CostCenterID ?? null,
            BudgetStartDate: request.budgetStartDate,
            BudgetEndDate: request.budgetEndDate,
            JumpStart: false
        }));
        this.log.info({
            type: 'ProjectBudgetService:putPeriodDisbursement',
            hours,
            amount,
            periodCount: updates.length,
            updates
        });
        return this.putBudgetList(updates);
    }

    private putBudgetList(items: unknown[]): Observable<unknown> {
        return this.http.put(this.uri, {
            ProjectBudgetList: items
        });
    }

    private getMappedBudgets(
        contractListId: string,
        budgetStartDate: string | null,
        budgetEndDate: string | null
    ): Observable<any[]> {
        return this.http
            .post<any>(this.uri, {
                ContractListID: contractListId,
                BudgetStartDate: budgetStartDate,
                BudgetEndDate: budgetEndDate
            })
            .pipe(map(resp => this.parseBudgetGetResponse(resp)));
    }

    private parseBudgetGetResponse(resp: unknown): any[] {
        let rows = resp as any;
        if (typeof rows === 'string') {
            rows = JSON.parse(rows);
        }
        if (!Array.isArray(rows)) {
            rows = rows?.Items || [];
        }
        return (rows || []).map((row: any) => this.mapBudgetRow(row));
    }

    private mapBudgetRow(item: any): any {
        const mapped = { ...item };
        const keys = Object.keys(item || {});
        const dispCols = keys
            .filter(x => x !== 'Row_Version')
            .filter(x => x.includes('_'));
        const dynCols = dispCols.reduce((a, c) => {
            const k = c.split('_')[0];
            const k1 = c.split('_')[1];
            const cur = a[k] || {};
            cur[k1] = item[c];
            return {
                ...a,
                [k]: cur
            };
        }, {} as Record<string, any>);
        dispCols.forEach(x => delete mapped[x]);
        return {
            ...mapped,
            ...dynCols
        };
    }

    private getPeriodColumns(item: any): string[] {
        return getDynamicColumns(item, this.filterItems).filter(
            col => item[col]?.TxnID
        );
    }

    private findCreatedBudget(
        budgets: any[],
        item: Record<string, unknown>
    ): any | undefined {
        const list = Array.isArray(budgets) ? budgets : [];
        const matches = list.filter(budget =>
            this.sameBudgetIdentity(budget, item)
        );
        const emptyMatches = matches.filter(budget =>
            this.isEmptyBudget(budget)
        );
        if (emptyMatches.length) {
            return emptyMatches[emptyMatches.length - 1];
        }
        if (matches.length) {
            return matches[matches.length - 1];
        }
        const emptyRows = list.filter(budget => this.isEmptyBudget(budget));
        return emptyRows[emptyRows.length - 1];
    }

    private sameBudgetIdentity(
        left: Record<string, unknown>,
        right: Record<string, unknown>
    ): boolean {
        return (
            this.sameIdentityValue(left.TaskListID, right.TaskListID) &&
            this.sameIdentityValue(left.CostType, right.CostType) &&
            this.sameIdentityValue(left.SIListID, right.SIListID) &&
            this.sameIdentityValue(left.PersonListID, right.PersonListID) &&
            this.sameIdentityValue(left.PIListID, right.PIListID)
        );
    }

    private sameIdentityValue(left: unknown, right: unknown): boolean {
        const normalizedLeft = this.normalizeIdentityValue(left);
        const normalizedRight = this.normalizeIdentityValue(right);
        return normalizedLeft === normalizedRight;
    }

    private normalizeIdentityValue(value: unknown): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        return String(value).toLowerCase();
    }

    private isEmptyBudget(item: any): boolean {
        const cols = getDynamicColumns(item, this.filterItems);
        if (!cols.length) {
            return true;
        }
        return cols.every(
            col => !Number(item[col]?.Hours) && !Number(item[col]?.Amount)
        );
    }
}
